import { formatUserFacingError } from '@/lib/errors'
import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { ticketingService } from '../services/ticketing.service'
import type { CurrentOwnerOrder, CurrentOwnerTicket } from '../types'

interface UseMyTicketsOptions {
  ownerAccessToken?: string
  ticketId?: string
}

export function useMyTickets({ ownerAccessToken, ticketId }: UseMyTicketsOptions = {}) {
  const { user, loading: authLoading } = useAuth()
  const [tickets, setTickets] = useState<CurrentOwnerTicket[]>([])
  const [orders, setOrders] = useState<CurrentOwnerOrder[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (authLoading) return
    if (!user && !ownerAccessToken) {
      setTickets([])
      setOrders([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)
    try {
      const result = await ticketingService.getCurrentOwnerTickets({
        uid: user?.uid,
        email: user?.email,
        ticketId,
        ownerAccessToken,
      })
      setTickets(result.tickets)
      setOrders(result.orders)
    } catch (err: unknown) {
      setError(formatUserFacingError(err, 'Failed to load your tickets.'))
    } finally {
      setLoading(false)
    }
  }, [authLoading, ownerAccessToken, ticketId, user])

  useEffect(() => {
    let active = true
    void refresh().finally(() => {
      if (!active) return
    })
    return () => {
      active = false
    }
  }, [refresh])

  return { tickets, orders, loading, error, refresh }
}
